import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  createVisitReport,
  uploadVisitPhoto,
  listVisitReports,
  sendReportToGuardian,
  fileUrl,
  adminLogin,
  getTeamToken,
  setTeamToken,
} from "../lib/api";
import {
  HeartPulse,
  Droplet,
  ReceiptText,
  Send,
  Plus,
  Check,
  ExternalLink,
  ArrowLeft,
  Loader2,
  Lock,
  LogOut,
} from "lucide-react";
import logo from "../assets/logo.png";

const CATS = [
  { id: "bp", label: "BP reading pic", icon: HeartPulse },
  { id: "sugar", label: "Sugar test pic", icon: Droplet },
  { id: "medicine_bill", label: "Medical bill pic", icon: ReceiptText },
];

export default function RepresentativePortal() {
  const [authed, setAuthed] = useState(!!getTeamToken());
  const [creds, setCreds] = useState({ email: "", password: "" });
  const [loggingIn, setLoggingIn] = useState(false);
  const [reports, setReports] = useState([]);
  const [active, setActive] = useState(null); // current report being filled
  const [creating, setCreating] = useState(false);
  const [sendingId, setSendingId] = useState(null);
  const [uploading, setUploading] = useState("");
  const [form, setForm] = useState({
    parent_name: "",
    guardian_name: "",
    guardian_email: "",
    visit_date: new Date().toISOString().split("T")[0],
    bp_reading: "",
    sugar_reading: "",
    notes: "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = async () => {
    try {
      setReports(await listVisitReports());
    } catch (e) {
      if (e?.response?.status === 401) {
        setTeamToken(null);
        setAuthed(false);
      }
    }
  };
  useEffect(() => {
    if (authed) load();
  }, [authed]);

  const login = async () => {
    if (!creds.email || !creds.password) {
      toast.error("Enter email and password.");
      return;
    }
    setLoggingIn(true);
    try {
      const { token } = await adminLogin(creds.email, creds.password);
      setTeamToken(token);
      setAuthed(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Login failed.");
    } finally {
      setLoggingIn(false);
    }
  };

  const logout = () => {
    setTeamToken(null);
    setAuthed(false);
  };

  const createReport = async () => {
    if (!form.parent_name || !form.guardian_name || !form.guardian_email) {
      toast.error("Parent name, guardian name and guardian email are required.");
      return;
    }
    setCreating(true);
    try {
      const r = await createVisitReport(form);
      setActive(r);
      toast.success("Report started. Now attach the photos.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create report.");
    } finally {
      setCreating(false);
    }
  };

  const onUpload = async (category, fileList) => {
    if (!active || !fileList?.length) return;
    setUploading(category);
    try {
      for (const file of Array.from(fileList)) {
        const photo = await uploadVisitPhoto(active.id, category, file);
        setActive((a) => ({ ...a, photos: [...(a.photos || []), photo] }));
      }
      toast.success("Photo uploaded.");
      load();
    } catch (e) {
      toast.error("Upload failed.");
    } finally {
      setUploading("");
    }
  };

  const send = async (id) => {
    setSendingId(id);
    try {
      await sendReportToGuardian(id);
      toast.success("Visit photos sent to the guardian by email.");
      load();
      if (active?.id === id) setActive((a) => ({ ...a, sent_to_guardian: true }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send.");
    } finally {
      setSendingId(null);
    }
  };

  const resetNew = () => {
    setActive(null);
    setForm({
      parent_name: "",
      guardian_name: "",
      guardian_email: "",
      visit_date: new Date().toISOString().split("T")[0],
      bp_reading: "",
      sugar_reading: "",
      notes: "",
    });
  };

  const countByCat = (photos, cat) =>
    (photos || []).filter((p) => p.category === cat).length;

  if (!authed) {
    return (
      <div className="min-h-screen bg-brand-cream flex items-center justify-center px-6">
        <div className="w-full max-w-sm rounded-3xl bg-white border border-border p-8" data-testid="report-login">
          <div className="flex items-center gap-3 mb-6">
            <img src={logo} alt="ElderCare24" className="h-10 w-auto" />
            <div>
              <p className="font-serif text-2xl leading-none text-brand-ink">
                Eldercare<span className="text-brand">24</span>
              </p>
              <p className="text-xs text-brand-ink/50 mt-1">Representative login</p>
            </div>
          </div>
          <p className="text-sm text-brand-ink/60 mb-5 flex items-center gap-1.5">
            <Lock size={14} className="text-brand" /> Staff only — visit photos & client details.
          </p>
          <div className="space-y-3">
            <div>
              <Label className="text-brand-ink/80 text-sm mb-1.5 block">Email</Label>
              <Input
                data-testid="report-login-email"
                type="email"
                value={creds.email}
                onChange={(e) => setCreds((c) => ({ ...c, email: e.target.value }))}
                placeholder="admin@eldercare24.com"
              />
            </div>
            <div>
              <Label className="text-brand-ink/80 text-sm mb-1.5 block">Password</Label>
              <Input
                data-testid="report-login-password"
                type="password"
                value={creds.password}
                onChange={(e) => setCreds((c) => ({ ...c, password: e.target.value }))}
                onKeyDown={(e) => e.key === "Enter" && login()}
                placeholder="••••••••"
              />
            </div>
            <Button
              data-testid="report-login-btn"
              onClick={login}
              disabled={loggingIn}
              className="w-full rounded-full bg-brand hover:bg-brand-hover text-white h-11"
            >
              {loggingIn ? "Signing in..." : "Sign in"}
            </Button>
            <Link to="/" className="block text-center text-sm text-brand-ink/50 hover:text-brand mt-2">
              Back to site
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-cream">
      <header className="bg-brand-ink text-brand-cream">
        <div className="max-w-6xl mx-auto px-6 py-6 flex items-center justify-between">
          <div>
            <p className="font-serif text-2xl">
              Eldercare<span className="text-brand-gold">24</span>
            </p>
            <p className="text-xs text-brand-cream/60">Representative portal · Jaipur</p>
          </div>
          <div className="flex items-center gap-4">
            <Link
              to="/"
              data-testid="back-home-link"
              className="text-sm text-brand-cream/70 hover:text-brand-gold flex items-center gap-1"
            >
              <ArrowLeft size={16} /> Back to site
            </Link>
            <button
              data-testid="report-logout-btn"
              onClick={logout}
              className="text-sm text-brand-cream/70 hover:text-brand-gold flex items-center gap-1"
            >
              <LogOut size={15} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10 grid lg:grid-cols-5 gap-8">
        {/* Left: create + upload */}
        <section className="lg:col-span-3 space-y-6">
          <div className="rounded-2xl bg-white border border-border p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-serif text-2xl text-brand-ink">
                {active ? "Visit report" : "New visit report"}
              </h2>
              {active && (
                <Button
                  data-testid="new-report-btn"
                  variant="outline"
                  size="sm"
                  onClick={resetNew}
                  className="rounded-full"
                >
                  <Plus size={15} /> New
                </Button>
              )}
            </div>

            {!active ? (
              <div className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-brand-ink/80 text-sm mb-1.5 block">Parent's name</Label>
                    <Input data-testid="rep-parent-name" value={form.parent_name} onChange={(e) => set("parent_name", e.target.value)} placeholder="e.g. Sita Mehta" />
                  </div>
                  <div>
                    <Label className="text-brand-ink/80 text-sm mb-1.5 block">Visit date</Label>
                    <Input data-testid="rep-visit-date" type="date" value={form.visit_date} onChange={(e) => set("visit_date", e.target.value)} />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-brand-ink/80 text-sm mb-1.5 block">Guardian's name</Label>
                    <Input data-testid="rep-guardian-name" value={form.guardian_name} onChange={(e) => set("guardian_name", e.target.value)} placeholder="e.g. Rohan Mehta" />
                  </div>
                  <div>
                    <Label className="text-brand-ink/80 text-sm mb-1.5 block">Guardian's email</Label>
                    <Input data-testid="rep-guardian-email" type="email" value={form.guardian_email} onChange={(e) => set("guardian_email", e.target.value)} placeholder="guardian@email.com" />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-brand-ink/80 text-sm mb-1.5 block">BP reading</Label>
                    <Input data-testid="rep-bp" value={form.bp_reading} onChange={(e) => set("bp_reading", e.target.value)} placeholder="e.g. 130/85" />
                  </div>
                  <div>
                    <Label className="text-brand-ink/80 text-sm mb-1.5 block">Sugar test</Label>
                    <Input data-testid="rep-sugar" value={form.sugar_reading} onChange={(e) => set("sugar_reading", e.target.value)} placeholder="e.g. 110 mg/dL" />
                  </div>
                </div>
                <div>
                  <Label className="text-brand-ink/80 text-sm mb-1.5 block">Notes (optional)</Label>
                  <Textarea data-testid="rep-notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2} placeholder="How are they doing today?" />
                </div>
                <Button data-testid="rep-create-btn" onClick={createReport} disabled={creating} className="rounded-full bg-brand hover:bg-brand-hover text-white px-6">
                  {creating ? "Creating..." : "Start report & add photos"}
                </Button>
              </div>
            ) : (
              <div className="space-y-5">
                <div className="text-sm text-brand-ink/70">
                  For <b className="text-brand-ink">{active.parent_name}</b> · guardian{" "}
                  <b className="text-brand-ink">{active.guardian_name}</b> ({active.guardian_email})
                </div>

                <div className="grid sm:grid-cols-3 gap-3">
                  {CATS.map((c) => {
                    const Icon = c.icon;
                    const n = countByCat(active.photos, c.id);
                    return (
                      <div key={c.id} className="rounded-xl border border-border p-4 text-center">
                        <Icon className="mx-auto text-brand mb-2" size={22} />
                        <p className="text-sm font-medium text-brand-ink mb-1">{c.label}</p>
                        <p className="text-xs text-brand-ink/50 mb-3">{n} uploaded</p>
                        <label
                          data-testid={`upload-${c.id}`}
                          className="inline-flex items-center gap-1 text-xs font-semibold text-brand cursor-pointer border border-brand/40 rounded-full px-3 py-1.5 hover:bg-brand/5"
                        >
                          {uploading === c.id ? (
                            <Loader2 size={13} className="animate-spin" />
                          ) : (
                            <Plus size={13} />
                          )}
                          Add photo
                          <input
                            type="file"
                            accept="image/*"
                            multiple={c.id === "medicine_bill"}
                            className="hidden"
                            onChange={(e) => onUpload(c.id, e.target.files)}
                          />
                        </label>
                      </div>
                    );
                  })}
                </div>

                {active.photos?.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {active.photos.map((p) => (
                      <img
                        key={p.id}
                        src={fileUrl(p.url)}
                        alt={p.category}
                        className="h-16 w-16 object-cover rounded-lg border border-border"
                      />
                    ))}
                  </div>
                )}

                <div className="flex items-center gap-3 pt-2 border-t border-border">
                  <Button
                    data-testid="rep-send-btn"
                    onClick={() => send(active.id)}
                    disabled={sendingId === active.id || !active.photos?.length}
                    className="rounded-full bg-brand-ink hover:bg-brand-ink/90 text-white px-6"
                  >
                    {sendingId === active.id ? "Sending..." : <><Send size={15} /> Send to guardian</>}
                  </Button>
                  <a
                    href={`/visit/${active.share_token}`}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="rep-preview-link"
                    className="text-sm text-brand hover:underline flex items-center gap-1"
                  >
                    Preview guardian page <ExternalLink size={14} />
                  </a>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Right: reports table with Send column */}
        <section className="lg:col-span-2">
          <div className="rounded-2xl bg-white border border-border p-6">
            <h2 className="font-serif text-xl text-brand-ink mb-4">Recent visits</h2>
            {reports.length === 0 ? (
              <p className="text-sm text-brand-ink/50">No reports yet.</p>
            ) : (
              <div className="divide-y divide-border" data-testid="reports-list">
                {reports.map((r) => (
                  <div key={r.id} className="py-3 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-brand-ink truncate">{r.parent_name}</p>
                      <p className="text-xs text-brand-ink/50 truncate">
                        {r.visit_date} · {r.photos.length} pics · {r.guardian_name}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {r.sent_to_guardian ? (
                        <Badge className="bg-brand-sage/20 text-brand-sage border-0 gap-1">
                          <Check size={12} /> Sent
                        </Badge>
                      ) : (
                        <Button
                          data-testid={`send-col-${r.id}`}
                          size="sm"
                          onClick={() => send(r.id)}
                          disabled={sendingId === r.id || r.photos.length === 0}
                          className="rounded-full bg-brand hover:bg-brand-hover text-white h-8 px-3 text-xs"
                        >
                          <Send size={12} /> Send
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>
    </div>
  );
}
